import type { Dispatch, SetStateAction } from 'react';
import {
  CheckCircle2,
  PenTool,
  Type,
  CheckSquare,
  Image as ImageIcon,
  ChevronDown,
} from 'lucide-react';

export interface SignerFieldListField {
  id: string;
  label: string;
  required: boolean;
  fieldType?: string;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string | boolean;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    customMessage?: string;
  };
}

export interface SignerFieldValue {
  type: 'draw' | 'type' | 'upload' | 'text_input' | 'checkbox' | 'image_upload';
  data: string;
  fontFamily?: string;
}

interface SignerFieldListProps {
  fields: SignerFieldListField[];
  signedFields: Map<string, SignerFieldValue>;
  setSignedFields: Dispatch<SetStateAction<Map<string, SignerFieldValue>>>;
  onSignatureFieldClick: (field: SignerFieldListField) => void;
}

export function SignerFieldList({
  fields,
  signedFields,
  setSignedFields,
  onSignatureFieldClick,
}: SignerFieldListProps) {
  return (
    <div className="space-y-2">
      {fields.map((field) => {
        const isSigned = signedFields.has(field.id);
        const ft = field.fieldType || 'signature';

        if (ft === 'dropdown') {
          const currentValue = signedFields.get(field.id)?.data || '';
          const opts = field.options ?? [];
          return (
            <div
              key={field.id}
              className="rounded-lg border border-gray-200 bg-white p-2"
              title={field.helpText}
            >
              <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <ChevronDown className="h-3 w-3" />
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <select
                value={currentValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setSignedFields((prev) => {
                    const next = new Map(prev);
                    if (val) {
                      next.set(field.id, { type: 'text_input', data: val });
                    } else {
                      next.delete(field.id);
                    }
                    return next;
                  });
                }}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
              >
                <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}…`}</option>
                {opts.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {opts.length === 0 && (
                <p className="mt-1 text-[11px] text-amber-600">
                  No options configured for this field.
                </p>
              )}
            </div>
          );
        }

        if (ft === 'text') {
          const currentValue =
            signedFields.get(field.id)?.data ??
            (typeof field.defaultValue === 'string' ? field.defaultValue : '');
          return (
            <div
              key={field.id}
              className="rounded-lg border border-gray-200 bg-white p-2"
              title={field.helpText}
            >
              <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <Type className="h-3 w-3" />
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={currentValue}
                maxLength={field.validation?.maxLength}
                pattern={field.validation?.pattern}
                onChange={(e) => {
                  const val = e.target.value;
                  setSignedFields((prev) => {
                    const next = new Map(prev);
                    if (val) {
                      next.set(field.id, { type: 'text_input', data: val });
                    } else {
                      next.delete(field.id);
                    }
                    return next;
                  });
                }}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}…`}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
              />
            </div>
          );
        }

        if (ft === 'checkbox') {
          const stored = signedFields.get(field.id);
          const isChecked = stored
            ? stored.data === 'true'
            : field.defaultValue === true;
          return (
            <div
              key={field.id}
              className={`flex items-center gap-3 rounded-lg border p-2 cursor-pointer select-none ${
                isChecked
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-white'
              }`}
              onClick={() => {
                setSignedFields((prev) => {
                  const next = new Map(prev);
                  const newChecked = !isChecked;
                  next.set(field.id, {
                    type: 'checkbox',
                    data: String(newChecked),
                  });
                  return next;
                });
              }}
            >
              <div
                className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
                  isChecked
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-400'
                }`}
              >
                {isChecked && (
                  <CheckSquare className="h-3.5 w-3.5 text-white" />
                )}
              </div>
              <span
                className={`text-sm ${
                  isChecked ? 'text-green-700' : 'text-gray-600'
                }`}
              >
                {field.label}
                {field.required && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </span>
            </div>
          );
        }

        if (ft === 'image') {
          return (
            <div
              key={field.id}
              className={`rounded-lg border p-2 ${
                isSigned
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <ImageIcon className="h-3 w-3" />
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                  {isSigned ? 'Change image' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const base64 = ev.target?.result as string;
                        setSignedFields((prev) => {
                          const next = new Map(prev);
                          next.set(field.id, {
                            type: 'image_upload',
                            data: base64,
                          });
                          return next;
                        });
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
                {isSigned && (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                )}
              </div>
            </div>
          );
        }

        const buttonLabel = ft === 'initials' ? 'Initial' : 'Sign';
        const FieldIcon = ft === 'initials' ? Type : PenTool;
        return (
          <div
            key={field.id}
            className={`flex items-center justify-between rounded-lg border p-2 ${
              isSigned
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span
              className={`flex items-center gap-1.5 text-sm ${
                isSigned ? 'text-green-700' : 'text-gray-600'
              }`}
            >
              <FieldIcon className="h-3.5 w-3.5" />
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </span>
            {isSigned ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <button
                onClick={() => onSignatureFieldClick(field)}
                className="rounded bg-primary-500 px-2 py-1 text-xs font-medium text-white hover:bg-primary-600"
              >
                {buttonLabel}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
